import { type LucideIcon } from 'lucide-react'
import { KpiCard } from './KpiCard'

// ============================================================================
// Types
// ============================================================================

export interface KpiGroupProps {
  title: string
  icon: LucideIcon
  kpis: Array<{ label: string; actual?: number; goal?: number; key: string; icon?: LucideIcon }>
  isLoading?: boolean
}

// ============================================================================
// KpiGroup Component
// ============================================================================

/**
 * KpiGroup Component
 *
 * Displays a group of KPI cards with a section title and icon.
 * Groups are visually separated by vertical spacing only (no divider lines).
 *
 * Features:
 * - Icon rendered inline before the group title
 * - Equal-width KPI cards in responsive CSS grid layout
 * - Automatic column layout based on number of cards
 * - Bottom margin for spacing between groups
 * - Loading state passed through to child KpiCard components
 *
 * @param title - The group title (e.g., "Network Deployment")
 * @param icon - Lucide icon component to display before title
 * @param kpis - Array of KPI data objects with label, value, and key
 * @param isLoading - Whether the KPI data is loading
 */
export function KpiGroup({ title, icon: Icon, kpis, isLoading = false }: KpiGroupProps) {
  // Determine grid columns based on number of KPIs
  const gridColsClass = kpis.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'

  return (
    <div className="mb-8">
      {/* Group Header: Icon + Title */}
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-[#212d47]" />
        <h2 className="text-base font-semibold text-[#212d47]">
          {title}
        </h2>
      </div>

      {/* KPI Cards Grid */}
      <div className={`grid ${gridColsClass} gap-4 md:gap-6`}>
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.key}
            actual={kpi.actual}
            goal={kpi.goal}
            label={kpi.label}
            icon={kpi.icon}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  )
}
